import { useState, useEffect, useCallback } from 'react'
import toast from 'react-hot-toast'
import { fetchWithAuth } from '../../libs/apiUtils'
import Modal from '../Modal'
import { CheckIcon, ShieldCheckIcon, UserIcon } from '@heroicons/react/24/outline'

interface TablePermissionsModalProps {
  isOpen: boolean
  onClose: () => void
  table: { id?: string; name?: string }
}

interface UserPermission {
  id: string
  fullName: string
  email: string
  role: string
  hasPermission: boolean
}

export default function TablePermissionsModal({
  isOpen,
  onClose,
  table
}: TablePermissionsModalProps) {
  const [users, setUsers] = useState<UserPermission[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState<string | null>(null)

  const loadPermissions = useCallback(async () => {
    if (!table.id) return
    try {
      setLoading(true)
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/table-permissions/${table.id}`, {
        method: 'GET'
      })
      const data = await res.json()
      if (res.ok && data.success) {
        setUsers(data.data)
      }
    } catch (err) {
      toast.error('Gagal memuat data izin')
    } finally {
      setLoading(false)
    }
  }, [table.id])

  useEffect(() => {
    if (isOpen && table.id) {
      loadPermissions()
    }
  }, [isOpen, table.id, loadPermissions])

  async function togglePermission(userId: string, currentHas: boolean) {
    if (!table.id) return
    try {
      setSaving(userId)
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/table-permissions/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tableId: table.id,
          userId,
          active: !currentHas
        })
      })
      const data = await res.json()
      if (res.ok && data.success) {
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, hasPermission: !currentHas } : u))
        toast.success('Izin diperbarui')
      } else {
        toast.error(data.message || 'Gagal memperbarui izin')
      }
    } catch (err) {
      toast.error('Kesalahan jaringan')
    } finally {
      setSaving(null)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="mb-6">
        <h3 className="text-xl font-bold text-neutral-800 flex items-center gap-2">
          <ShieldCheckIcon className="size-6 text-primary-600" />
          Izin Akses Pengguna
        </h3>
        <p className="text-sm text-neutral-500 mt-1">
          Kelola siapa saja yang dapat mengakses tabel <span className="font-bold text-neutral-700">{table.name}</span>
        </p>
      </div>

      {loading ? (
        <div className="py-20 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto"></div>
        </div>
      ) : users.length === 0 ? (
        <div className="py-12 text-center bg-neutral-50 rounded-2xl border border-dashed border-neutral-200">
          <UserIcon className="size-12 text-neutral-300 mx-auto mb-3" />
          <p className="text-neutral-500 font-medium">Tidak ada pengguna tersedia</p>
        </div>
      ) : (
        <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar">
          {users.map((user) => (
            <div key={user.id} className="bg-white border border-neutral-100 rounded-xl p-4 hover:shadow-md transition-all group">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="bg-primary-100 p-2 rounded-lg text-primary-600">
                    <UserIcon className="size-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-neutral-800 text-sm">{user.fullName}</h4>
                    <p className="text-xs text-neutral-400">
                      {user.email} • <span className="uppercase font-bold text-primary-500">{user.role}</span>
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => togglePermission(user.id, user.hasPermission)}
                  disabled={saving === user.id}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    user.hasPermission ? 'bg-primary-600' : 'bg-neutral-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      user.hasPermission ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 flex justify-end">
        <button
          onClick={onClose}
          className="bg-neutral-900 text-white px-6 py-2 rounded-xl font-bold hover:bg-neutral-800 active:scale-95 transition-all shadow-lg shadow-neutral-200 flex items-center gap-2"
        >
          <CheckIcon className="size-5" />
          Selesai
        </button>
      </div>
    </Modal>
  )
}
